import type { HsMaterial } from '../../graphic-render-new/materials/material.class';
import type { HsDefaultShader } from '../../graphic-render-new/shaders/classes/default-shader.class';
import type { HsShader } from '../../graphic-render-new/shaders/classes/shader.class';

/**
 * Scene materials by key, and the shader programs they share. A large BIM scene has thousands of material
 * colors but few distinct shaders; materials with the same shader source and clipping storage reuse one
 * compiled program.
 */
export class HsSceneMaterialCache {
  private readonly materials: Map<string, HsMaterial> = new Map();
  private readonly sharedShaders: Map<string, HsShader[]> = new Map();
  private programs: number = 0;

  /** Distinct shader programs behind the cached materials. */
  public get programCount(): number {
    return this.programs;
  }

  public get size(): number {
    return this.materials.size;
  }

  /** The material for `key`, created with `create` on first use. */
  public get<T extends HsMaterial>(key: string, create: () => T): T {
    const cached: HsMaterial | undefined = this.materials.get(key);
    if (cached !== undefined) {
      return cached as T;
    }
    const material: T = create();
    this.materials.set(key, material);
    this.programs = new Set([...this.materials.values()].map((entry: HsMaterial) => entry.getShader())).size;
    return material;
  }

  /**
   * An already compiled shader with the same type, transparency, source and clipping storage, or `shader`
   * itself when it is the first of its kind. `compile` says whether the material must compile it.
   */
  public share<T extends HsShader>(shader: T): { shader: T; compile: boolean } {
    const key: string = `${shader.shaderType}|${
      shader.hasTransparency
    }|${shader.makeVertexCode()}\n${shader.makeFragmentCode()}`;
    const candidates: HsShader[] = this.sharedShaders.get(key) ?? [];
    // Clipping planes are uniforms read from the storage instance, so only shaders sharing it are interchangeable.
    const clippingStorage = (candidate: HsShader): unknown =>
      (candidate as Partial<HsDefaultShader>).clippingStorage ?? null;
    const existing: HsShader | undefined = candidates.find(
      (candidate: HsShader) =>
        candidate.constructor === shader.constructor && clippingStorage(candidate) === clippingStorage(shader)
    );
    if (existing !== undefined) {
      return { shader: existing as T, compile: false };
    }
    candidates.push(shader);
    this.sharedShaders.set(key, candidates);
    return { shader, compile: true };
  }

  /** Forgets every material; with `gl`, also deletes their shader programs. */
  public clear(gl: WebGL2RenderingContext | null): void {
    if (gl !== null) {
      const shaders: Set<HsShader> = new Set<HsShader>();
      for (const material of this.materials.values()) {
        shaders.add(material.getShader());
      }
      for (const shader of shaders) {
        shader.deleteProgram(gl);
      }
    }
    this.materials.clear();
    this.sharedShaders.clear();
    this.programs = 0;
  }
}
